import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const stats = [
  { value: 2021, suffix: "", label: "Established", testid: "counter-years" },
  { value: 5, suffix: "+ yrs", label: "Years running", testid: "counter-running" },
  { value: 150, suffix: "+", label: "Clients served", testid: "counter-projects" },
  { value: 8, suffix: "", label: "Practice areas", testid: "counter-domains" },
];

const Counter = ({ value, suffix, label, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return (
    <div ref={ref} data-testid={testid} className="flex flex-col">
      <span className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-none">
        <span className="text-gradient-cyan">{n}</span>
        <span className="text-foreground/80">{suffix}</span>
      </span>
      <span className="mt-2 text-sm text-muted-foreground">{label}</span>
    </div>
  );
};

const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-6 lg:sticky lg:top-28 self-start"
        >
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#0A82D8]">
            About DMASKUT
          </span>
          <h2 className="mt-3 font-display font-semibold text-4xl md:text-5xl tracking-tighter leading-[1.05]">
            Built in 2021.<br />
            <span className="text-gradient-cyan">Still thinking like a startup.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            DMASKUT Consultancy started with one simple idea — stop sending clients
            to five different firms for work that could be done under one roof.
            Today we operate across eight practice areas, supporting founders,
            families and established businesses alike.
          </p>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            No big-firm airs, no endless proposals. Clear conversations, honest
            timelines, and work that shows up on time.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-start gap-3 p-4 rounded-2xl glass">
              <div className="mt-1 h-2 w-2 rounded-full bg-[#00B4F0]" />
              <div>
                <div className="font-medium text-sm">How we work</div>
                <div className="text-xs text-muted-foreground mt-0.5">One point of contact, full visibility.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-2xl glass">
              <div className="mt-1 h-2 w-2 rounded-full bg-[#0A82D8]" />
              <div>
                <div className="font-medium text-sm">What you get</div>
                <div className="text-xs text-muted-foreground mt-0.5">Fixed scope, fixed fee, clear deadlines.</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-6 relative"
        >
          <div className="relative glass-strong rounded-3xl p-8 md:p-10 overflow-hidden">
            <div className="grid grid-cols-2 gap-8">
              {stats.map((s) => (
                <Counter key={s.label} {...s} />
              ))}
            </div>
            <div className="mt-10 pt-8 border-t border-border">
              <blockquote className="font-display text-lg md:text-xl leading-snug tracking-tight">
                "We'd rather say 'we can't help here' than take on work we can't do
                properly. That's how we've kept clients for years, not months."
              </blockquote>
              <div className="mt-4 text-sm text-muted-foreground">
                — Kamlesh Kumar Sinha, Founder
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
